import { useState } from "react";
import { styles } from "../styles";
import { hasCapability } from "../utils/capabilities";

/**
 * Delete / Move controls for service tickets.
 *
 * UNSIGNED: Delete + Move both visible to anyone with canManage. Operations
 * fix the auto-fed Estimated Bill lines via the data-layer batch actions
 * (deleteServiceTicket / moveTicket).
 *
 * SIGNED: only the Delete button shows, and only for users who hold the
 * deleteSignedTickets capability (admin/manager tier — default-on for
 * admin, can be revoked from manager; explicit grant required for tech).
 * Routes through the deleteSignedServiceTicket Cloud Function so it can
 * also wipe the fed timeEntries + guestLabor that came out of the ticket
 * — undoing the wrong-job mistake completely.
 */
export default function TicketActions({
  ticket,
  jobs = [],
  actions,
  canManage,
  role,
  capabilities,
}) {
  const [moveTo, setMoveTo] = useState("");
  const [busy, setBusy] = useState(false);

  const isSigned = ticket.status === "signed";
  const canDeleteSigned = hasCapability(
    role,
    capabilities,
    "deleteSignedTickets"
  );

  // Signed tickets: only show Delete, only if capability granted.
  // Unsigned tickets: show Delete + Move for any manager.
  if (isSigned) {
    if (!canDeleteSigned) return null;
  } else if (!canManage) {
    return null;
  }

  const otherJobs = jobs.filter((j) => j.id !== ticket.jobId);

  const handleDelete = async () => {
    const confirmText = isSigned
      ? "Delete this SIGNED ticket?\n\n" +
        "This is the 'wrong job, undo' escape hatch. It will:\n" +
        "  • Remove the ticket from your archive\n" +
        "  • Remove its labor/material lines from the job's Estimated Bill\n" +
        "  • Delete the time entries it created on the time board / payroll\n" +
        "  • Remove its guest-labor entries from the job\n\n" +
        "The facility may see a 'deleted' marker on their copy. " +
        "This cannot be undone."
      : "Delete this ticket?\n\n" +
        "Its labor/material lines will also be removed from the job's " +
        "Estimated Bill. This cannot be undone.";
    const ok = window.confirm(confirmText);
    if (!ok) return;
    setBusy(true);
    try {
      if (isSigned) {
        await actions.deleteSignedTicket(ticket.id);
      } else {
        await actions.deleteServiceTicket(ticket.id);
      }
    } catch (e) {
      window.alert(`Delete failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async () => {
    if (!moveTo) return;
    const target = jobs.find((j) => j.id === moveTo);
    const ok = window.confirm(
      `Move this ticket to "${target?.title || "that job"}"?\n\nIts bill lines move with it. Equipment selection is cleared (re-select on the new job if needed).`
    );
    if (!ok) return;
    await actions.moveTicket(ticket.id, moveTo);
    setMoveTo("");
  };

  // Fragment so the parent can lay Delete on the same flex row as the
  // other action buttons (Edit + Review&Sign). The Move workflow is
  // rendered alongside but flexWrap on the parent keeps it usable on
  // narrow screens.
  return (
    <>
      <button
        style={{ ...styles.button, color: "red" }}
        onClick={handleDelete}
        disabled={busy}
      >
        {busy ? "Deleting…" : isSigned ? "Delete (signed)" : "Delete"}
      </button>{" "}
      {!isSigned && (
        <>
          <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            <option value="">Move to job…</option>
            {otherJobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.jobNumber ? `${j.jobNumber} — ${j.title}` : j.title}
              </option>
            ))}
          </select>{" "}
          <button style={styles.button} onClick={handleMove} disabled={!moveTo}>
            Move
          </button>
        </>
      )}
    </>
  );
}
